import { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";
import { useTranslation } from "react-i18next"; // eklendi

const REVIEWS = [
  {
    name: "Nivine Abou El Zelof",
    date: "2025-02-27", // ham tarih formatı
    textKey: "reviews.nivine",
    avatarBg: "bg-[#ff7849]",
  },
  {
    name: "Lars Gebhard",
    date: "2025-02-25",
    textKey: "reviews.lars",
    avatarBg: "bg-[#7c3aed]",
  },
  {
    name: "Sergio Pérez Alcañiz",
    date: "2025-02-25",
    textKey: "reviews.sergio",
    avatarBg: "bg-[#06b6d4]",
  },
  {
    name: "Aleksandra K.",
    date: "2025-02-22",
    textKey: "reviews.aleksandra",
    avatarBg: "bg-[#f59e0b]",
  },
  {
    name: "Murat A.",
    date: "2025-02-18",
    textKey: "reviews.murat",
    avatarBg: "bg-[#10b981]",
  },
  {
    name: "Sophie R.",
    date: "2025-02-12",
    textKey: "reviews.sophie",
    avatarBg: "bg-[#ef4444]",
  },
];

function usePerPage() {
  const [perPage, setPerPage] = useState(3);
  useEffect(() => {
    const handle = () => {
      const w = window.innerWidth;
      if (w < 768) setPerPage(1);
      else if (w < 1024) setPerPage(2);
      else setPerPage(3);
    };
    handle();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);
  return perPage;
}

export default function ReviewsSlider() {
  const { t } = useTranslation(); // eklendi
  const perPage = usePerPage();
  const totalPages = useMemo(
    () => Math.ceil(REVIEWS.length / perPage),
    [perPage]
  );
  const [page, setPage] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [totalPages, page]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let sx = 0, dx = 0;
    const start = (e) => { sx = e.touches[0].clientX; };
    const end = (e) => {
      dx = e.changedTouches[0].clientX - sx;
      if (Math.abs(dx) > 40) dx < 0 ? goNext() : goPrev();
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchend", end, { passive: true });
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchend", end);
    };
  }, [page, totalPages]);

  const goPrev = () => setPage((p) => Math.max(0, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  const translatePct = -(page * 100);

  return (
    <section className="w-full bg-[#0b1224] pt-6 px-4">
      <div className="max-w-7xl mx-auto" id="fark">
        {/* Başlık */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-5xl font-bold text-[#e8d1b9]">
            {t("reviews.title")}
          </h2>
          <h3 className="text-2xl md:text-4xl font-semibold text-white/90 mt-2">
            {t("reviews.subtitle")}
          </h3>
          <p className="text-white/70 mt-4 max-w-3xl mx-auto">
            {t("reviews.description")}
          </p>
        </div>

        {/* Slider alanı */}
        <div className="relative">
          {/* Sol Ok */}
          <button
            onClick={goPrev}
            aria-label={t("common.prev")}
            disabled={page === 0}
            className="absolute -left-3 md:-left-6 top-1/2 -translate-y-1/2 z-10
              w-8 h-8 md:w-10 md:h-10 rounded-full
              bg-gradient-to-b from-[#d4af37] to-[#b8860b]
              shadow-lg text-white flex items-center justify-center
              hover:shadow-xl disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
          >
            <HiOutlineChevronLeft size={24} />
          </button>

          {/* Sağ Ok */}
          <button
            onClick={goNext}
            aria-label={t("common.next")}
            disabled={page === totalPages - 1}
            className="absolute -right-3 md:-right-6 top-1/2 -translate-y-1/2 z-10
              w-8 h-8 md:w-10 md:h-10 rounded-full
              bg-gradient-to-b from-[#d4af37] to-[#b8860b]
              shadow-lg hover:shadow-xl text-white flex items-center justify-center
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
          >
            <HiOutlineChevronRight size={24} />
          </button>

          {/* Track */}
          <div className="overflow-hidden">
            <div
              ref={trackRef}
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(${translatePct}%)` }}
            >
              {REVIEWS.map((r, i) => (
                <div
                  key={i}
                  className="px-3 flex-shrink-0"
                  style={{ flex: `0 0 ${100 / perPage}%` }}
                >
                  <ReviewCard {...r} t={t} />
                </div>
              ))}
            </div>
          </div>

          {/* Dots */}
          <div className="mt-6 flex items-center justify-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`h-2 rounded-full transition-all ${
                  i === page ? "w-6 bg-white" : "w-2 bg-white/40"
                }`}
                aria-label={`${t("common.page")} ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ReviewCard({ name, date, textKey, avatarBg, t }) {
  const formattedDate = new Date(date).toLocaleDateString(t("langCode"), {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="h-full rounded-2xl bg-white shadow-xl p-6 flex flex-col" >
      <div className="flex items-center gap-3" >
        <div
          className={`w-10 h-10 rounded-full ${avatarBg} text-white grid place-items-center font-semibold`}
        >
          {name.charAt(0)}
        </div>
        <div>
          <div className="font-semibold text-[#111827]">{name}</div>
          <div className="text-xs text-[#6b7280]">{formattedDate}</div>
        </div>
      </div>
      <div className="mt-4 text-[#f59e0b] text-lg leading-none">{"★★★★★"}</div>
      <p className="mt-3 text-[#374151] leading-relaxed flex-1">
        {t(textKey)}
      </p>
    </div>
  );
}
